import { isNullishOrEmpty } from "@grindex-client/common/js/helpers/typeHelpers";
import { createXylView } from "@grindex-client/common/js/helpers/xyl-view-helpers";
import type { $TSFIXME } from "@grindex-client/common/js/types/typescript-migration";
import { asyncModules } from "@grindex-client/components/Module/asyncModule";
import { screwButtonsSettings } from "@grindex-common/helpers/constants";
import { hideElement, isTagOfType, showElement } from "@grindex-common/helpers/domManipulation";
import { findSelectionValueCaseInsensitive, setSelected } from "@grindex-common/helpers/dropDowns";
import { events } from "@grindex-common/helpers/events";
import { getQueryParams } from "@grindex-client/common/js/helpers/query-parser";
import { executeScriptElements } from "@grindex-common/helpers/scriptsHelpers";
import { XylemCookies } from "@grindex-common/helpers/XylemCookies.class";

(function listenForEvent() {
	asyncModules("marketoForm", events.TAB_LOAD_EVENTS.productOverview, init);
})();

function init(views: HTMLElement[]): void {
	const SELECTOR_BRAND_DD = "select[data-fieldname='Xylem_Brand']";
	const SELECTOR_COUNTRY_DD = "select[data-fieldname='CountryName']";
	function getMarketoPrefillApi(trackingCookie: string, fields: string): string {
		return `/api/marketoform/getformprefill/?trackingCookie=${trackingCookie}&fields=${fields}`;
	}
	const DONE = 4;
	const OK = 200;
	const cookies = new XylemCookies();

	for (const view of views) {
		const form = view.querySelector("form");
		const formid = view.getAttribute("data-embedform-id");

		if (!isTagOfType(form, "form")) {
			continue;
		}

		if (!window.MktoForms2) {
			executeScriptElements(view, function () {
				initMarketoForms(formid);
				setDefaults(form, view);
			});
			continue;
		}

		initMarketoForms(formid);
		setDefaults(form, view);
	}

	function setDefaults($form: HTMLFormElement, $view: HTMLElement): void {
		const $validation = $form.querySelector(".fe-validation-msg") as HTMLElement | null;

		if ($validation) {
			hideElement($validation);
		}

		if (window.MktoForms2) {
			initMarketoDefaultValues();
		}

		setBrandDefaultValue();

		setCountryDefaultValue();

		initFormElementStyling($view);

		// For prefill, need to wipe any text area's as they should not be pre-filled
		$view.querySelectorAll("textarea").forEach(($textArea) => {
			$textArea.value = "";
		});
	}

	function initMarketoDefaultValues(): void {
		const currentBrand = getBrandDefaultValue();
		const currentCountry = getCountryDefaultValue();

		window.MktoForms2.whenReady(function (form: $TSFIXME) {
			const defaultValues = form.getValues();

			const brandValue = defaultValues.Xylem_Brand === "" ? currentBrand : defaultValues.Xylem_Brand;

			var xylemBrandSelectEl = document.getElementById("Xylem_Brand") as HTMLSelectElement | null;
			var countryNameSelectEl = document.getElementById("countryName") as HTMLSelectElement | null;

			const currentCountryCaseAdjusted = findSelectionValueCaseInsensitive(countryNameSelectEl, currentCountry);
			const brandValueCaseAdjusted = findSelectionValueCaseInsensitive(xylemBrandSelectEl, brandValue);

			form.vals({
				xylemConversionURL: window.location.href,
				countryName: currentCountryCaseAdjusted,
			});

			if (brandValueCaseAdjusted) {
				form.vals({
					Xylem_Brand: brandValueCaseAdjusted,
				});
			}

			const formId = `mktoForm_${form.getId()}`;

			modifyCheckboxStyle(formId);

			if ((window.config.marketoForm!.disableFormPrefill as unknown as string).toLowerCase() === "false") {
				fetchMarketoFormPrefillData(formId);
			}

			form.onSuccess(function ($form: $TSFIXME) {
				const $formWrapper = document.querySelector(`#mktoForm_${$form.formid}`)!.closest(".form-wrapper") as HTMLElement;

				const $thankYouElement = $formWrapper.parentElement!.querySelector(".thank-you-element") as HTMLElement;
				submitGtmData($form);
				const redirectUrl = $formWrapper.dataset["url"];

				if (redirectUrl !== "") {
					window.location.href = redirectUrl!;
				} else {
					hideElement($formWrapper);
					showElement($thankYouElement);
				}

				return false;
			});
		});
	}

	function submitGtmData(form: $TSFIXME): void {
		let formName = "";
		const $h1 = document.getElementsByTagName("h1")[0];
		if ($h1) {
			formName = $h1.textContent ?? "";
		}

		const submissionData = {
			event: "page.submit",
			"page.submit": {
				v: 12,
				name: formName,
				data: {
					"Form Id": form.Form_ID,
					company: form.Company,
					industry: form.Industry,
					brand: form.Xylem_Brand,
					"request type": form.Request_Type__c,
					country: form.countryName,
					"job title": form.Title,
					return_customer: form.Existing_Customer_Indicated_by_Lead__c,
					email_opt_in: form.GDPR_Consent__c,
					"state name": form.stateName,
					"SFC Personal Company Question": form.sFCPersonalCompanyQuestion,
					City: form.City,
					"Xylem Lead Question": form.Xylem_Lead_Question,
				},
				type: "lead",
				done: true,
				result: {
					message: "",
				},
				record: {
					recordType: "lead",
					id: "",
				},
			},
		};

		(window.dataLayer = window.dataLayer || []).push(submissionData);
	}

	function modifyCheckboxStyle(formid: string): void {
		document.querySelectorAll(`#${formid} input[type='checkbox']`).forEach(($checkBox) => {
			const label = document.querySelector(`label[for='${($checkBox as HTMLInputElement).id}']`);
			if (label) {
				label.classList.add("marginleft");
			}
		});
	}

	function initFormElementStyling($view: HTMLElement): void {
		initCheckboxes($view);
		initRadioButtons($view);
		initEpiCheckboxes();
	}

	function initMarketoForms(formId: string | null): void {
		if (formId) {
			const munchkinId = window.config.marketoForm!.munchkinId!;
			const podUrl =
				munchkinId === "240-UTB-146"
					? "//info.xylem.com"
					: munchkinId === "306-CJR-109"
						? "//go.sensus.com"
						: "//info.xyleminc.com";

			(window.MktoForms2 as $TSFIXME).loadForm(podUrl, munchkinId, formId, function (form: $TSFIXME) {
				form.onSubmit(function () {
					if ("marketoForms" in window) {
						window.marketoForms({
							form: form,
							id: formId,
						});
					}
				});
			});
		}
	}

	function initCheckboxes($view: HTMLElement): void {
		$view.querySelectorAll("input[type='checkbox']").forEach(($checkBox) => {
			$($checkBox).screwDefaultButtons(screwButtonsSettings(true));
		});
	}

	function initRadioButtons($view: HTMLElement): void {
		$view.querySelectorAll("input[type='radio']").forEach(($radioButton) => {
			$($radioButton).screwDefaultButtons(screwButtonsSettings(false));
		});
	}

	function initEpiCheckboxes(): void {
		// EpiForms: Add no-margin class when checkboxes are next to each other
		document.querySelectorAll("div.FormChoice.FormChoice--Checkbox").forEach(($checkBox) => {
			const $sibling = $checkBox.nextElementSibling;

			if ($sibling && $sibling.classList.contains("FormChoice--Checkbox")) {
				$checkBox.classList.add("no-margin");
			}
		});
	}

	function getBrandDefaultValue(): string {
		const params = getQueryParams();
		let brandValue: string | undefined;

		if (params["brand"]) {
			brandValue = params["brand"].replace(/\+/g, " ");
			brandValue = decodeURIComponent(brandValue!).toLowerCase();
		}

		if (!brandValue) {
			brandValue = window.config.brand?.name;
		}

		return brandValue ? toTitleCase(brandValue) : "";
	}

	function toTitleCase(source: string): string {
		return source.replace(/\w[^\s-]*/g, function (txt) {
			return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
		});
	}

	function getCountryDefaultValue(): string {
		const currentCountry = document.documentElement.dataset["country"];

		return currentCountry ? toTitleCase(currentCountry) : "";
	}

	function setBrandDefaultValue(): void {
		const brandValue = getBrandDefaultValue();
		const $selectElement = document.querySelector(SELECTOR_BRAND_DD) as HTMLSelectElement | null;

		if ($selectElement) {
			setSelected($selectElement, brandValue);
		}
	}

	function setCountryDefaultValue(): void {
		const currentCountry = getCountryDefaultValue();
		const $selectElement = document.querySelector(SELECTOR_COUNTRY_DD) as HTMLSelectElement | null;

		if (!$selectElement) {
			return;
		}

		setSelected($selectElement, currentCountry);
	}

	function fetchMarketoFormPrefillData(formid: string): void {
		const marketoCookie = cookies.getCookieValue("_mkto_trk");
		const formElements = getFormElements(formid);
		const formElementsName = getFormElementsName(formElements);

		if (!isNullishOrEmpty(marketoCookie) && !isNullishOrEmpty(formElementsName)) {
			const requestURL = getMarketoPrefillApi(encodeURIComponent(marketoCookie), formElementsName);

			const httpRequest = new XMLHttpRequest();
			httpRequest.onreadystatechange = function () {
				if (httpRequest.readyState === DONE) {
					if (httpRequest.status === OK) {
						const data = JSON.parse(httpRequest.response);
						if (data && data.success) {
							fillform(data.result, formElements as NodeListOf<HTMLElement>);
						}
					}
				}
			};

			httpRequest.open("GET", requestURL, true);
			httpRequest.send();
		}
	}

	function getFormElements(formid: string): NodeListOf<HTMLElement> | "" {
		if (formid) {
			const form = (document.forms as $TSFIXME)[formid] as HTMLFormElement | undefined;
			if (form) {
				return form.querySelectorAll("textarea, select,input:not([type='hidden']");
			}
		}
		return "";
	}

	function getFormElementsName(formElements: NodeListOf<HTMLElement> | ""): string {
		if (formElements && formElements.length > 0) {
			return Array.from(formElements)
				.map(function (element) {
					return (element as HTMLInputElement).name;
				})
				.join(",");
		}
		return "";
	}

	function fillform(result: $TSFIXME[], formElements: NodeListOf<HTMLElement>): void {
		const arrFormElements = Array.from(formElements) as HTMLInputElement[];
		if (result && result.length > 0) {
			Object.entries(result[0]).forEach(([key, value]) => {
				const match = arrFormElements.filter((element) => element.name.toLowerCase() === key.toLowerCase());

				if (match.length > 0 && !match[0]!.value) {
					match[0]!.value = value as string;
				}
			});
		}
	}
}

export const marketoForm = createXylView({
	view: "marketoForm",
	init,
});
